// src/components/EntityFinancials/EntityFinancialsTable.js
import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../api';
import { AVAILABLE_YEARS } from '../../utils/incomeConstants';
import { calculateCorporateTax } from '../../utils/incomeCalculations';
import AddFinancialsModal from './AddFinancialsModal';
import ConfirmModal from '../Common/ConfirmModal';
import { useConfirm } from '../../hooks/useConfirm';
import './EntityFinancialsTable.css';

const EntityFinancialsTable = ({ entityId, entityName, taxRegime }) => {
  const [financials, setFinancials] = useState([]);
  const [selectedYear, setSelectedYear] = useState(AVAILABLE_YEARS[0]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFinancials, setEditingFinancials] = useState(null);

  // Confirm modal state
  const { confirmState, showConfirm, hideConfirm, handleConfirm } = useConfirm();

  const loadFinancials = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiService.fetchEntityFinancials(entityId);
      setFinancials(data);
    } catch (error) {
      console.error('Error loading financials:', error);
    } finally {
      setIsLoading(false);
    }
  }, [entityId]);

  useEffect(() => {
    loadFinancials();
  }, [loadFinancials]);

  const calculateResults = (data) => {
    const revenue = data.revenue || 0;
    const otherIncome = data.otherIncome || data.other_income || 0;
    const purchasesExpenses = data.purchasesExpenses || data.purchases_expenses || 0;
    const personnelCosts = data.personnelCosts || data.personnel_costs || 0;
    const otherExpenses = data.otherExpenses || data.other_expenses || 0;
    const depreciation = data.depreciation || 0;
    const financialResult = data.financialResult || data.financial_result || 0;
    const exceptionalResult = data.exceptionalResult || data.exceptional_result || 0;
    const isPME = data.isPME !== undefined ? data.isPME : (data.is_pme !== undefined ? data.is_pme : true);

    const totalRevenue = revenue + otherIncome;
    const totalExpenses = purchasesExpenses + personnelCosts + otherExpenses + depreciation;
    const operatingProfit = totalRevenue - totalExpenses;
    const taxableProfit = operatingProfit + financialResult + exceptionalResult;

    let corporateTax = 0;
    let netProfit = taxableProfit;

    if (taxRegime === 'IS' && taxableProfit > 0) {
      const taxResult = calculateCorporateTax(taxableProfit, isPME, data.year || selectedYear);
      corporateTax = taxResult.totalTax;
      netProfit = taxableProfit - corporateTax;
    }

    return {
      operatingProfit,
      taxableProfit,
      corporateTax,
      netProfit
    };
  };

  const handleAddFinancials = async (data) => {
    try {
      const results = calculateResults(data);
      await apiService.createEntityFinancials(entityId, {
        ...data,
        ...results
      });
      await loadFinancials();
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding financials:', error);
    }
  };

  const handleUpdateFinancials = async (data) => {
    try {
      const results = calculateResults(data);
      await apiService.updateEntityFinancials(editingFinancials.id, {
        ...data,
        ...results
      });
      await loadFinancials();
      setEditingFinancials(null);
    } catch (error) {
      console.error('Error updating financials:', error);
    }
  };

  const handleDeleteFinancials = (id, year) => {
    showConfirm({
      title: 'Supprimer ces données financières ?',
      message: `Voulez-vous vraiment supprimer les données financières de l'année ${year} ?`,
      confirmText: 'Supprimer',
      type: 'danger',
      onConfirm: async () => {
        try {
          await apiService.deleteEntityFinancials(id);
          await loadFinancials();
        } catch (error) {
          console.error('Error deleting financials:', error);
        }
      }
    });
  };

  const filteredFinancials = financials.filter(f => f.year === selectedYear);
  const currentData = filteredFinancials[0];

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  if (isLoading) {
    return <div className="financials-loading">Chargement des données financières...</div>;
  }

  return (
    <div className="financials-table-container">
      <div className="financials-header">
        <h4>Données financières - {entityName}</h4>
        <div className="financials-actions">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="year-selector"
          >
            {AVAILABLE_YEARS.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          {!currentData && (
            <button
              className="add-financials-btn"
              onClick={() => setShowAddModal(true)}
            >
              + Ajouter exercice {selectedYear}
            </button>
          )}
        </div>
      </div>

      {!currentData ? (
        <div className="no-financials">
          Aucune donnée financière pour {selectedYear}
        </div>
      ) : (
        <div className="financials-details">
          <div className="financials-section">
            <h5>Compte de résultat</h5>
            <table className="financials-table">
              <tbody>
                <tr>
                  <td>Chiffre d'affaires</td>
                  <td className="amount">{formatCurrency(currentData.revenue)}</td>
                </tr>
                <tr>
                  <td>Autres produits</td>
                  <td className="amount">{formatCurrency(currentData.other_income)}</td>
                </tr>
                <tr className="subtotal">
                  <td><strong>Total produits</strong></td>
                  <td className="amount"><strong>{formatCurrency((currentData.revenue || 0) + (currentData.other_income || 0))}</strong></td>
                </tr>
                <tr>
                  <td>Achats et charges externes</td>
                  <td className="amount negative">-{formatCurrency(currentData.purchases_expenses)}</td>
                </tr>
                <tr>
                  <td>Charges de personnel</td>
                  <td className="amount negative">-{formatCurrency(currentData.personnel_costs)}</td>
                </tr>
                <tr>
                  <td>Autres charges</td>
                  <td className="amount negative">-{formatCurrency(currentData.other_expenses)}</td>
                </tr>
                <tr>
                  <td>Amortissements</td>
                  <td className="amount negative">-{formatCurrency(currentData.depreciation)}</td>
                </tr>
                <tr className="subtotal">
                  <td><strong>Résultat d'exploitation</strong></td>
                  <td className={`amount ${currentData.operating_profit >= 0 ? 'positive' : 'negative'}`}>
                    <strong>{formatCurrency(currentData.operating_profit)}</strong>
                  </td>
                </tr>
                <tr>
                  <td>Résultat financier</td>
                  <td className="amount">{formatCurrency(currentData.financial_result)}</td>
                </tr>
                <tr>
                  <td>Résultat exceptionnel</td>
                  <td className="amount">{formatCurrency(currentData.exceptional_result)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="financials-section">
            <h5>Résultat fiscal</h5>
            <table className="financials-table">
              <tbody>
                <tr className="highlight">
                  <td><strong>Résultat avant impôt</strong></td>
                  <td className={`amount ${currentData.taxable_profit >= 0 ? 'positive' : 'negative'}`}>
                    <strong>{formatCurrency(currentData.taxable_profit)}</strong>
                  </td>
                </tr>
                {taxRegime === 'IS' && (
                  <tr>
                    <td>Impôt sur les sociétés {currentData.is_pme ? '(PME)' : ''}</td>
                    <td className="amount negative">-{formatCurrency(currentData.corporate_tax)}</td>
                  </tr>
                )}
                <tr className="total">
                  <td><strong>Résultat net</strong></td>
                  <td className={`amount ${currentData.net_profit >= 0 ? 'positive' : 'negative'}`}>
                    <strong>{formatCurrency(currentData.net_profit)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="financials-section">
            <h5>Affectation du résultat</h5>
            <table className="financials-table">
              <tbody>
                <tr>
                  <td>Mise en réserve</td>
                  <td className="amount">{formatCurrency(currentData.reserves)}</td>
                </tr>
                <tr>
                  <td>Dividendes distribués</td>
                  <td className="amount dividend">{formatCurrency(currentData.dividends)}</td>
                </tr>
                {currentData.dividends > 0 && (
                  <>
                    <tr className="date-row">
                      <td>Date AG (décision)</td>
                      <td className="date-value">
                        {currentData.dividend_decision_date
                          ? new Date(currentData.dividend_decision_date).toLocaleDateString('fr-FR')
                          : '-'}
                      </td>
                    </tr>
                    <tr className="date-row">
                      <td>Date versement</td>
                      <td className="date-value">
                        {currentData.dividend_payment_date
                          ? new Date(currentData.dividend_payment_date).toLocaleDateString('fr-FR')
                          : '-'}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>

          <div className="financials-actions-row">
            <button
              className="edit-btn"
              onClick={() => setEditingFinancials(currentData)}
            >
              Modifier
            </button>
            <button
              className="delete-btn"
              onClick={() => handleDeleteFinancials(currentData.id, selectedYear)}
            >
              Supprimer
            </button>
          </div>
        </div>
      )}

      {showAddModal && (
        <AddFinancialsModal
          year={selectedYear}
          taxRegime={taxRegime}
          onSave={handleAddFinancials}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {editingFinancials && (
        <AddFinancialsModal
          year={selectedYear}
          taxRegime={taxRegime}
          financials={editingFinancials}
          onSave={handleUpdateFinancials}
          onClose={() => setEditingFinancials(null)}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        onClose={hideConfirm}
        onConfirm={handleConfirm}
        title={confirmState.title}
        message={confirmState.message}
        details={confirmState.details}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
      />
    </div>
  );
};

export default EntityFinancialsTable;
